import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { MultiSelectDropDownOption } from '@replai-platform/ui-components';
import * as SDK from '@replai-platform/sdk';
import type { RootState } from '../../../store/rootReducer';
import useGetCountries from '../../../api/hooks/countries/useGetCountries';
import { ProjectActions } from '../../../store/project';
import { COUNTRY_GROUP_OPTION_ID_PREFIX, getCountryLabel, groupCountries } from '../../../utils/countryGroups';

const useCountryFilter = ({
  selectedCountries,
}: {
  selectedCountries: string[];
}): {
  countryFilterOptions: MultiSelectDropDownOption[];
  getUpdatedCountries: (options: MultiSelectDropDownOption[]) => string[];
  hasData: boolean;
  isLoading: boolean;
  isInitialFilter: boolean;
  setIsInitialFilter: (i: boolean) => void;
} => {
  const dispatch = useDispatch();
  const projectId = useSelector((state: RootState) => state.project.id);
  const projectBaseMetric = useSelector((state: RootState) => state.project.baseMetric);
  const isInitialFilter = useRef(true);

  const { since, until } = SDK.getDateRangeForRecommendations();

  const setIsInitialFilter = (i: boolean) => {
    isInitialFilter.current = i;
  };

  // Fetch and update project networks
  const { isSuccess, data, isLoading } = useGetCountries({
    projectIds: [projectId],
    metrics: [projectBaseMetric],
    orderBy: {
      condition: SDK.OrderByCondition.DESC_NULLS_LAST,
      value: projectBaseMetric,
    },
    metricsFilters: {
      dateStartTimestamp: since.getTime(),
      dateEndTimestamp: until.getTime(),
      countriesToExclude: ['ALL'],
    },
  });

  const mappedCountries = useMemo(
    () =>
      data?.countriesMetrics
        ?.filter((countryMetric) => (countryMetric.metrics[projectBaseMetric] ?? 0) > 0)
        .map((countryMetric) => ({
          country: countryMetric.country,
          spend: countryMetric.metrics.spend ?? 0,
        })) ?? [],
    [data, projectBaseMetric]
  );

  useEffect(() => {
    if (isSuccess && mappedCountries?.length > 0) {
      dispatch(ProjectActions.changeCountries(mappedCountries));
    }
  }, [dispatch, isSuccess, mappedCountries]);

  // Create filter options, bucketed by geo group. Countries keep the order they were fetched in
  // (sorted by the project's base metric), so the top markets show up first within each group.
  const countryFilterOptions = useMemo<MultiSelectDropDownOption[]>(
    () =>
      isSuccess && data
        ? [
            {
              label: 'All Countries',
              isAllOption: true,
              selected: selectedCountries.length === mappedCountries.length,
            },
            ...groupCountries(mappedCountries.map(({ country }) => country)).flatMap((group) => [
              {
                id: `${COUNTRY_GROUP_OPTION_ID_PREFIX}${group.id}`,
                label: group.label,
                isGroupOption: true,
                groupId: group.id,
              },
              ...group.countries.map((country) => ({
                id: country,
                label: getCountryLabel(country),
                groupId: group.id,
                selected: selectedCountries.includes(country),
              })),
            ]),
          ]
        : [],
    [mappedCountries, data, isSuccess, selectedCountries]
  );

  // Turns what the dropdown reports into the new country filter value. The dropdown only knows
  // about the countries it was given, so any other selected country (picked in another country
  // filter, or under a date range where it still had spend) is outside its scope and is kept as
  // is - otherwise using one dropdown silently un-checks countries in the other.
  const getUpdatedCountries = useCallback(
    (options: MultiSelectDropDownOption[]) => {
      const knownCountries = new Set(mappedCountries.map(({ country }) => country));
      const selectedInDropDown = options
        .filter(({ isAllOption, isGroupOption, selected }) => selected && !isAllOption && !isGroupOption)
        .map(({ id }) => id)
        .filter((id): id is string => id !== undefined);
      // `ALL` is the "no country filter" sentinel, never a country to preserve
      const outOfScope = selectedCountries.filter((country) => country !== 'ALL' && !knownCountries.has(country));

      return [...outOfScope, ...selectedInDropDown];
    },
    [mappedCountries, selectedCountries]
  );

  return {
    countryFilterOptions,
    getUpdatedCountries,
    isLoading,
    hasData: countryFilterOptions.length > 0,
    setIsInitialFilter,
    isInitialFilter: isInitialFilter.current,
  };
};

export default useCountryFilter;
